import { RouterController } from "../routerClient";
import { StartupData, CompleteStartupData } from "../Startup/types";
import { Startup } from "../Startup/Startup";
import { AuthenticationToken } from "../Interop/types";
import { getRouterConfig } from "./RouterConfig";
import { LoggerClient as Logger } from "../logger";
import { AuthSignatureData, authSignature } from "../../services/Interop/AuthSignVerify";
import { FinsembleDesktopAgent } from "../Interop/FinsembleDesktopAgent";
import uuidv4 from "uuid-random";

/**
 * Starts Finsemble for "freestanding" apps (those that are not running under Electron). This is used
 * by the finsemble-javascript-adapter.
 */

export class FSBLFreestanding {
	#fdc3: FinsembleDesktopAgent;

	constructor({ fdc3 }: { fdc3: FinsembleDesktopAgent }) {
		this.#fdc3 = fdc3;
	}

	/**
	 * Freestanding apps must call startApp() to start the FSBL and fdc3 client code.
	 *
	 * Startup configurations and data can be passed in the params. If not provided, defaults will be used
	 * according to the typedocs for StartupData.
	 */
	async startApp(params?: StartupData) {
		// Window name must be unique within Finsemble, so uuid is always added even if passed in params.
		// This also prevents a freestanding app from spoofing another window by taking its name.
		const windowName: string = `${params?.windowName ?? document?.location?.href ?? "freestandingApp"}-${uuidv4()}`;

		// If no appName is provided then use the windowName. The app will be treated anonymously.
		const appName = params?.appName ?? windowName;

		let authenticationToken: AuthenticationToken;

		// If JSON Web Token is passed in then use it in place of the digital signing key. The use case is for the JWT to be created on a server to avoid having to include the private key into the app.
		// Note that Finsemble will use JWT's iot field to internally set the token's timestamp -- the Finsemble TTL for a token is 30 seconds.
		// JWT optional expiration time is not used to avoid confusion with the static auth timestamp, plus JWT expiration times are typically in minutes, where Finsemble needs to enforce a short TTL.
		if (params?.jwt) {
			authenticationToken = {
				signatureStringType: "static-jwt",
				signatureString: params?.jwt,
				meta: {
					name: appName,
				},
			};
			//else if digital signing key provided, then creates the static AuthenticationToken that is necessary to instantiate interop.
		} else if (params?.digitalSigningKey) {
			authenticationToken = await this.staticAuthenticationToken(params?.digitalSigningKey, appName);
		} else {
			// If a digital signing key hasn't been provided then the default is used with type set to "unauthenticated".
			authenticationToken = {
				signatureStringType: "unauthenticated",
				signatureString: "",
				meta: {
					name: appName, // required field for FDC3 type
					appId: appName, // authentication is based on the appId
				},
			};
		}

		/**
		 * Finsemble's router client requires a windowName but otherwise it will start with defaults unless routerAddress
		 * has been set, in which case that will be set as the serverAddress for IAC (FinsembleTransport).
		 */

		const routerAddress = params?.routerAddress;
		const definitiveRouterConfig = getRouterConfig(
			routerAddress
				? {
						windowName,
						transportSettings: {
							FinsembleTransport: {
								serverAddress: routerAddress,
							},
						},
				  }
				: {
						windowName,
				  }
		);

		const definitiveStartupData: CompleteStartupData = {
			windowName,
			webContentsName: windowName,
			authenticationToken,
		};

		const startup = Startup.getInstance();
		startup.setStartupData(definitiveStartupData);
		await RouterController.start(definitiveRouterConfig);

		startup.setReady();
		Logger.start();

		// In freestanding apps, we always immediately connect to the interop service.
		this.#fdc3.start();
	}

	/**
	 * Builds and returns a static authentication token.
	 */
	private async staticAuthenticationToken(digitalSigningKey, name): Promise<AuthenticationToken> {
		// a timestamp is in the signed data, which will be used for TTL check
		const timestamp = Date.now();
		const signatureData: AuthSignatureData = { timestamp };
		const { signature } = await authSignature(digitalSigningKey, signatureData);

		return {
			signatureStringType: "static",
			timestamp,
			signatureString: signature,
			meta: {
				name,
				appId: name,
			},
		};
	}

	/**
	 * Stops the app.
	 */
	async stopApp() {
		this.#fdc3.stop();
		Logger.unregisterClient();
		RouterController.stop();
	}
}
